import type { SystemRolePermissionId } from '@clockwise/shared';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useShowsUnits, useUnitsWhere, useVisibleUnits } from '@/lib/business-units';

interface Props {
  /** The unit's id; '' when none is picked yet. */
  value: string;
  /** Present when the unit may be picked (adding a record); absent shows it
   *  read-only — a customer or role stays in its unit. */
  onChange?: (businessUnitId: string) => void;
  /** The screen's view permission: read-only, the field shows only when the
   *  caller sees these records in more than one unit (`useShowsUnits`). */
  view: SystemRolePermissionId;
  /** What adding takes: the picker offers only the units where the caller's
   *  role holds it (`unrestricted`: at scope All) — the server refuses the
   *  others. */
  add: SystemRolePermissionId;
  addUnrestricted?: boolean;
  error?: string;
  hint?: string;
  id?: string;
}

/**
 * The ONE Business Unit a customer or role belongs to (or an employee's
 * primary employer), in a form: a picker of the units where the caller may
 * add one, when adding; the unit's name, when editing. Hidden when there is
 * nothing to pick or tell apart — one unit to add to, or this kind of record
 * seen in one unit only. With one unit to add to, the form starts on it
 * (`useAddUnits`).
 */
export function BusinessUnitField({
  value,
  onChange,
  view,
  add,
  addUnrestricted,
  error,
  hint,
  id = 'businessUnitId',
}: Props) {
  const units = useVisibleUnits();
  const addable = useUnitsWhere(add, { unrestricted: addUnrestricted });
  const shows = useShowsUnits(view);
  if (onChange ? addable.length < 2 : !shows) return null;
  const name = units.find((u) => u.id === value)?.name;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>Business Unit</Label>
      {onChange ? (
        <Select value={value || undefined} onValueChange={onChange}>
          <SelectTrigger id={id} className="w-full" aria-invalid={Boolean(error)}>
            <SelectValue placeholder="Choose a Business Unit" />
          </SelectTrigger>
          <SelectContent>
            {addable.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <p id={id} className="text-sm">
          {name ?? '—'}
        </p>
      )}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
